import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Building, Building2, Landmark, Wrench, ArrowRight, Shield, Leaf, Euro, Users, ChevronRight } from "lucide-react";
import { useState } from "react";

export default function LandingPage() {
  const [activeSection, setActiveSection] = useState("beneficiaires");

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Navbar */}
      <nav className="navbar flex items-center justify-between" data-testid="navbar">
        <div className="flex items-center gap-2">
          <Leaf className="h-8 w-8 text-[#1A4D2E]" />
          <span className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            MesSubventions
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/login">
            <Button variant="ghost" className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10" data-testid="login-nav-btn">
              Connexion
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#1D1D1D] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Vérifiez votre éligibilité aux
            <span className="text-[#1A4D2E]"> aides à la rénovation</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Simulez gratuitement vos aides MaPrimeRénov', CEE et aides locales en quelques minutes. 
            Jusqu'à 90% de vos travaux financés.
          </p>
        </div>

        {/* Feature badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100">
            <Shield className="h-5 w-5 text-[#1A4D2E]" />
            <span className="text-sm font-medium text-slate-700">100% Gratuit</span>
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100">
            <Euro className="h-5 w-5 text-[#E07A5F]" />
            <span className="text-sm font-medium text-slate-700">Sans engagement</span>
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100">
            <Leaf className="h-5 w-5 text-[#81B29A]" />
            <span className="text-sm font-medium text-slate-700">Conseil personnalisé</span>
          </div>
        </div>

        {/* Section Selector Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-xl p-1.5 shadow-sm border border-slate-200">
            <button
              onClick={() => setActiveSection("beneficiaires")}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2
                ${activeSection === "beneficiaires" 
                  ? "bg-[#1A4D2E] text-white shadow-md" 
                  : "text-slate-600 hover:bg-slate-100"
                }`}
              data-testid="tab-beneficiaires"
            >
              <Users className="h-5 w-5" />
              <span>Bénéficiaires</span>
            </button>
            <button
              onClick={() => setActiveSection("partenaires")}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2
                ${activeSection === "partenaires" 
                  ? "bg-[#1A4D2E] text-white shadow-md" 
                  : "text-slate-600 hover:bg-slate-100"
                }`}
              data-testid="tab-partenaires"
            >
              <Wrench className="h-5 w-5" />
              <span>Artisans Partenaires</span>
            </button>
          </div>
        </div>

        {/* Beneficiaires Section */}
        {activeSection === "beneficiaires" && (
          <div className="animate-fade-in">
            <p className="text-center text-slate-600 mb-8 max-w-xl mx-auto">
              Que vous soyez un particulier, une copropriété, une entreprise ou une collectivité, 
              découvrez les aides disponibles pour vos projets de rénovation.
            </p>
            
            {/* Four Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
              {/* Particulier Card */}
              <Link to="/particulier" className="group" data-testid="particulier-entry">
                <div className="relative h-[340px] rounded-2xl overflow-hidden shadow-lg card-interactive">
                  <img 
                    src="https://images.unsplash.com/photo-1766603636700-e9d80473f40f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBlY28lMjBmcmllbmRseSUyMGhvbWUlMjBleHRlcmlvciUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3MTE5NzI1OHww&ixlib=rb-4.1.0&q=85"
                    alt="Maison éco-responsable"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                        <Home className="h-5 w-5" />
                      </div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Particulier
                      </h2>
                    </div>
                    <p className="text-white/90 text-sm mb-3">
                      Propriétaire ou locataire, vérifiez vos aides pour vos travaux de rénovation.
                    </p>
                    <div className="flex items-center gap-2 text-[#F2CC8F] font-semibold text-sm">
                      <span>Tester mon éligibilité</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Copropriété Card */}
              <Link to="/copropriete" className="group" data-testid="copropriete-entry">
                <div className="relative h-[340px] rounded-2xl overflow-hidden shadow-lg card-interactive">
                  <img 
                    src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Immeuble en copropriété"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                        <Building className="h-5 w-5" />
                      </div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Copropriété
                      </h2>
                    </div>
                    <p className="text-white/90 text-sm mb-3">
                      Syndic ou copropriétaire, financez la rénovation globale de votre immeuble.
                    </p>
                    <div className="flex items-center gap-2 text-[#F2CC8F] font-semibold text-sm">
                      <span>Simuler MPR Copro</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Entreprise Card */}
              <Link to="/entreprise" className="group" data-testid="entreprise-entry">
                <div className="relative h-[340px] rounded-2xl overflow-hidden shadow-lg card-interactive">
                  <img 
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Bâtiment d'entreprise moderne"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Entreprise
                      </h2>
                    </div>
                    <p className="text-white/90 text-sm mb-3">
                      PME, ETI ou grande entreprise, optimisez vos coûts énergétiques.
                    </p>
                    <div className="flex items-center gap-2 text-[#F2CC8F] font-semibold text-sm">
                      <span>Simuler nos aides</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Collectivité Card */}
              <Link to="/collectivite" className="group" data-testid="collectivite-entry">
                <div className="relative h-[340px] rounded-2xl overflow-hidden shadow-lg card-interactive">
                  <img 
                    src="https://images.unsplash.com/photo-1555685812-4b943f1cb0eb?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Bâtiment public"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                        <Landmark className="h-5 w-5" />
                      </div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Collectivité
                      </h2>
                    </div>
                    <p className="text-white/90 text-sm mb-3">
                      Commune, département ou région, financez vos projets publics.
                    </p>
                    <div className="flex items-center gap-2 text-[#F2CC8F] font-semibold text-sm">
                      <span>Découvrir les dispositifs</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Partenaires Section */}
        {activeSection === "partenaires" && (
          <div className="animate-fade-in">
            <p className="text-center text-slate-600 mb-8 max-w-xl mx-auto">
              Rejoignez notre réseau d'artisans partenaires et accédez à des projets qualifiés 
              dans votre région.
            </p>
            
            {/* Single Partner Card */}
            <div className="max-w-2xl mx-auto">
              <Link to="/partenaire" className="group" data-testid="partenaire-entry">
                <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-lg card-interactive">
                  <img 
                    src="https://images.unsplash.com/photo-1695886517917-348d9e1059e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBwcm9mZXNzaW9uYWwlMjBhcmNoaXRlY3QlMjB0YWJsZXQlMjBzaXRlfGVufDB8fHx8MTc3MTE5NzI2Mnww&ixlib=rb-4.1.0&q=85"
                    alt="Professionnel du bâtiment"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                        <Wrench className="h-8 w-8" />
                      </div>
                      <h2 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Artisan Partenaire
                      </h2>
                    </div>
                    <p className="text-white/90 mb-6">
                      Artisan RGE, rejoignez notre réseau et accédez à des leads qualifiés dans votre région.
                      Bénéficiez d'outils de gestion et de visibilité auprès de prospects motivés.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                        <ChevronRight className="h-4 w-4 text-[#F2CC8F]" />
                        <span className="text-sm">Leads qualifiés</span>
                      </div>
                      <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                        <ChevronRight className="h-4 w-4 text-[#F2CC8F]" />
                        <span className="text-sm">Zone géographique</span>
                      </div>
                      <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                        <ChevronRight className="h-4 w-4 text-[#F2CC8F]" />
                        <span className="text-sm">Inscription gratuite</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[#F2CC8F] font-semibold mt-6">
                      <span>Devenir partenaire</span>
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-center text-2xl font-semibold text-[#1D1D1D] mb-12" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Les aides auxquelles vous pourriez être éligible
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[#1A4D2E]">MA</span>
              </div>
              <h4 className="font-semibold text-lg text-[#1D1D1D] mb-2">MaPrimeRénov'</h4>
              <p className="text-slate-600 text-sm">Aide de l'État jusqu'à 20 000€ pour vos travaux</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#FBE9E7] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[#E07A5F]">CEE</span>
              </div>
              <h4 className="font-semibold text-lg text-[#1D1D1D] mb-2">Prime CEE</h4>
              <p className="text-slate-600 text-sm">Certificats d'économies d'énergie cumulables</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#E0F7FA] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[#00838F]">LOC</span>
              </div>
              <h4 className="font-semibold text-lg text-[#1D1D1D] mb-2">Aides Locales</h4>
              <p className="text-slate-600 text-sm">Région, département, commune selon votre zone</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Leaf className="h-6 w-6 text-[#1A4D2E]" />
            <span className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MesSubventions
            </span>
          </div>
          <p className="text-sm text-slate-500">
            © 2024 MesSubventions. Simulateur d'aides à la rénovation énergétique.
          </p>
        </div>
      </footer>
    </div>
  );
}
